import React, {
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { FieldType } from 'utils/enum';
import { Layout } from 'components/Form';
import {
  useBidTypeChoices,
  useEstimateTemplates,
  useRepChoices,
} from 'queries/company';
import { processSchema } from 'utils/form';
import { useEstimateFromTemplateFieldConfig } from 'queries/estimates';

export interface IFormValues {
  template: number;
  rep: number;
  bid_type: string;
}

export type TemplateFormRef = {
  getFormData: () => IFormValues | null;
  validate: () => boolean;
  checkValidity: () => boolean;
  handleSubmit: (
    onSubmit: (data: IFormValues) => void
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  reset: (values?: Partial<IFormValues>) => void;
  setValue: (name: keyof IFormValues, value: any) => void;
};

type FormProps = {
  jobId: string;
  loading?: boolean;
  showRepField?: boolean;
  showBidTypeField?: boolean;
  onRepSelect?: (rep: number) => void;
  onBidTypeSelect?: (bidType: string) => void;
  onFormChange?: () => void;
  // For config-based schema processing
  config?: any;
  defaultValues?: Partial<IFormValues>;
  // For shared values syncing
  sharedRep?: number;
  sharedBidType?: string;
  initialRep?: number;
  initialBidType?: string;
};

const TemplateForm = forwardRef<TemplateFormRef, FormProps>(
  (
    {
      jobId,
      loading = false,
      showRepField = true,
      showBidTypeField = true,
      onRepSelect,
      onBidTypeSelect,
      onFormChange,
      config: externalConfig,
      defaultValues,
      sharedRep,
      sharedBidType,
      initialRep,
      initialBidType,
    },
    ref
  ) => {
    const { data: fetchedConfig } = useEstimateFromTemplateFieldConfig(jobId);
    const config = externalConfig || fetchedConfig;

    const isUpdatingRepFromSharedRef = useRef(false);
    const isUpdatingBidTypeFromSharedRef = useRef(false);

    // Create schema
    const schema = useMemo(() => {
      const baseSchema = {
        template: yup.number(),
        rep: yup.number(),
        bid_type: yup.string(),
      };

      if (config) {
        return yup.object(processSchema(baseSchema, config));
      }

      // Simple schema for auto mode
      return yup.object({
        template: yup.number().required('Please select a template'),
        rep: yup.number().optional(),
        bid_type: yup.string().optional(),
      });
    }, [config]);

    const {
      control,
      formState: { errors },
      setValue,
      getValues,
      watch,
      handleSubmit,
      reset,
      setError,
    } = useForm<IFormValues>({
      resolver: yupResolver(schema),
      defaultValues: defaultValues || {
        rep: initialRep || sharedRep,
        bid_type: initialBidType || sharedBidType,
      },
    });

    // Watch for rep, bid_type and template changes
    const repValue = watch('rep');
    const bidTypeValue = watch('bid_type');
    const templateValue = watch('template');

    // Notify parent when form values change
    useEffect(() => {
      onFormChange?.();
    }, [templateValue, repValue, bidTypeValue, onFormChange]);

    // Sync from sharedRep to form (for auto mode)
    useEffect(() => {
      if (isUpdatingRepFromSharedRef.current) {
        isUpdatingRepFromSharedRef.current = false;
        return;
      }

      if (sharedRep && (!repValue || repValue !== sharedRep)) {
        isUpdatingRepFromSharedRef.current = true;
        setValue('rep', sharedRep, { shouldDirty: false });
      }
    }, [sharedRep, repValue, setValue]);

    // Sync from sharedBidType to form (for auto mode)
    useEffect(() => {
      if (isUpdatingBidTypeFromSharedRef.current) {
        isUpdatingBidTypeFromSharedRef.current = false;
        return;
      }

      if (sharedBidType && (!bidTypeValue || bidTypeValue !== sharedBidType)) {
        isUpdatingBidTypeFromSharedRef.current = true;
        setValue('bid_type', sharedBidType, { shouldDirty: false });
      }
    }, [sharedBidType, bidTypeValue, setValue]);

    // Initialize form with initial values (for auto mode)
    useEffect(() => {
      if (initialRep && !repValue) {
        setValue('rep', initialRep);
      }
      if (initialBidType && !bidTypeValue) {
        setValue('bid_type', initialBidType);
      }
    }, [initialRep, initialBidType, setValue, repValue, bidTypeValue]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getFormData: () => {
        const values = getValues();
        // Use shared values if form doesn't have them (auto mode)
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;

        if (!values.template) {
          setError('template', { message: 'Please select a template' });
          return null;
        }

        if (!finalRep || !finalBidType) {
          if (!finalRep && showRepField) {
            setError('rep', { message: 'Please select a rep' });
          }
          if (!finalBidType && showBidTypeField) {
            setError('bid_type', { message: 'Please select a bid type' });
          }
          return null;
        }

        return {
          ...values,
          rep: finalRep,
          bid_type: finalBidType,
        };
      },
      validate: () => {
        const values = getValues();
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;
        let isValid = true;

        if (!values.template) {
          setError('template', { message: 'Please select a template' });
          isValid = false;
        }

        if (!finalRep && showRepField) {
          setError('rep', { message: 'Please select a rep' });
          isValid = false;
        }

        if (!finalBidType && showBidTypeField) {
          setError('bid_type', { message: 'Please select a bid type' });
          isValid = false;
        }

        return isValid;
      },
      checkValidity: () => {
        const values = getValues();
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;

        if (!values.template) return false;
        if (!finalRep && showRepField) return false;
        if (!finalBidType && showBidTypeField) return false;

        return true;
      },
      handleSubmit,
      reset,
      setValue,
    }));

    const fields = useMemo(() => {
      const _fields: any[] = [
        {
          name: 'template',
          type: FieldType.AsyncSelect,
          forceDropdown: true,
          label: 'Select Template',
          placeholder: 'Select Template',
          error: errors.template?.message,
          control,
          optionsPromise: useEstimateTemplates,
        },
      ];

      if (showRepField) {
        _fields.push({
          name: 'rep',
          type: FieldType.AsyncSelect,
          forceDropdown: true,
          label: 'Select Rep',
          placeholder: 'Select rep',
          error: errors.rep?.message,
          control,
          optionsPromise: useRepChoices,
          postSelect: (selected: any) => {
            if (selected?.value && onRepSelect) {
              onRepSelect(selected.value);
            }
          },
        });
      }

      if (showBidTypeField) {
        _fields.push({
          name: 'bid_type',
          type: FieldType.AsyncSelect,
          forceDropdown: true,
          label: 'Select Bid Type',
          placeholder: 'Select Bid Type',
          error: errors.bid_type?.message,
          control,
          optionsPromise: useBidTypeChoices,
          orderByLabel: false,
          postSelect: (selected: any) => {
            if (selected?.value && onBidTypeSelect) {
              onBidTypeSelect(selected.value);
            }
          },
        });
      }

      return _fields;
    }, [
      errors,
      control,
      showRepField,
      showBidTypeField,
      onRepSelect,
      onBidTypeSelect,
    ]);

    return <Layout fields={fields} loading={loading} />;
  }
);

TemplateForm.displayName = 'TemplateForm';

export default TemplateForm;
